import { v4 } from "uuid";
import { defineStore } from "pinia";
import { markRaw, onBeforeUnmount, onMounted, shallowRef, watch } from "vue";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import type { TranscriptAnnotation } from "@/models/Annotation";

export type SelectionSource = { fileReference: string } & (
  | { kind: "document"; page: number; text: string }
  | { kind: "transcript"; startTime: number; endTime: number; utteranceList: TranscriptAnnotation["utteranceList"] }
);

/** A live reference to the selected content, read whenever the menu is positioned. */
export interface SelectionAnchor {
  getBoundingClientRect(): DOMRect;
  /** The element containing the selection, so the menu follows its scrolling ancestors and hides when detached. */
  contextElement?: Element;
}

/** Belongs to the active case file; it is cleared whenever the case route changes. */
export interface SelectionSnapshot {
  readonly id: string;
  readonly source: Readonly<SelectionSource>;
  readonly anchor: SelectionAnchor;
}

export interface SelectionSnapshotInput extends Omit<SelectionSnapshot, "id"> {
  /**
   * Ends the publisher's selection. Called whenever the snapshot is dismissed, but not when another snapshot replaces
   * it, since that would clear the replacing selection.
   */
  onDismiss: () => void;
}

export interface SelectionMenuActionContext {
  snapshot: SelectionSnapshot;
}

export interface SelectionMenuAction {
  id: string;
  label: string;
  icon?: string;
  /** Disables the action, explaining why beneath its label. */
  disabledReason?: string;
  run(context: SelectionMenuActionContext): void | Promise<void>;
}

export interface SelectionMenuContribution {
  id: string;
  label: string;
  /** Lower values appear first; contributions with equal order are sorted by ID. */
  order?: number;
  /** Evaluated reactively, so reactive state read here updates an open menu. */
  getActions(snapshot: SelectionSnapshot): readonly SelectionMenuAction[];
}

export const useCaseSelectionMenuStore = defineStore(
  "case-selection-menu",
  () => {
    const params = useCaseRouteParams();
    const snapshot = shallowRef<SelectionSnapshot | null>(null);
    const contributions = shallowRef<ReadonlyMap<string, SelectionMenuContribution>>(new Map());

    let onDismissSnapshot: (() => void) | undefined;

    // Contributions belong to mounted components and unregister themselves; only the selection is case state.
    function reset() {
      const onDismiss = onDismissSnapshot;
      snapshot.value = null;
      onDismissSnapshot = undefined;
      onDismiss?.();
    }

    function publish({ source, anchor, onDismiss }: SelectionSnapshotInput) {
      onDismissSnapshot = onDismiss;

      return (snapshot.value = Object.freeze({
        id: v4(),
        source: Object.freeze({ ...source }),
        anchor: markRaw(anchor),
      }));
    }

    function dismiss(id: string) {
      if (snapshot.value?.id !== id) return false;

      reset();
      return true;
    }

    function register(contribution: SelectionMenuContribution) {
      if (contributions.value.has(contribution.id)) {
        throw new Error(`Selection menu contribution "${contribution.id}" is already registered`);
      }

      const registered = markRaw(contribution);
      contributions.value = new Map(contributions.value).set(contribution.id, registered);

      return () => {
        if (contributions.value.get(contribution.id) !== registered) return;

        const next = new Map(contributions.value);
        next.delete(contribution.id);
        contributions.value = next;
      };
    }

    // Like a text selection, a snapshot does not survive moving to another case or file.
    watch(
      [() => params.value.client, () => params.value.brand, () => params.value.casename, () => params.value.casefile],
      reset,
    );

    return { snapshot, contributions, publish, dismiss, register, reset };
  },
  { scope: "case" },
);

export function useSelectionMenuContribution(contribution: SelectionMenuContribution) {
  const store = useCaseSelectionMenuStore();
  let unregister: (() => void) | undefined;

  onMounted(() => (unregister = store.register(contribution)));
  onBeforeUnmount(() => unregister?.());
}
